'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { play } from 'cuelume'
import { useSetAtom } from 'jotai'

import { useAuth } from '@/features/auth/hooks'
import type { OrderSource } from '@/lib/analytics'
import { trackSubmittedOrders } from '@/lib/analytics-order'
import { accountKeys } from '@/service/account.hook'
import {
  cancelOrder,
  createOrder,
  createOrderBatch,
  executeOrder,
  executeOrderBatch,
  updateOrder,
} from '@/service/execution.api'
import { patchOpenOrder, removeOpenOrder } from '@/service/ws/account-order-merge'
import { openOrdersRawAtom } from '@/state/account'
import { PublicAccount } from '@/types/generated/common/account'
import {
  CancelOrderParams,
  NewOrderParams,
  OrderExecutionCommand,
  OrderExecutionCommandList,
  UpdateOrderParams,
} from '@/types/generated/execution'
import { Success } from '@/types/generated/public_api'
import { ErrorResponse } from '@/types/service'

export const executionKeys = {
  all: ['execution'] as const,
  orders: () => [...executionKeys.all, 'orders'] as const,
}

export const useExecuteOrder = (props?: {
  onSuccess?: (data: Success, variables: OrderExecutionCommand) => void
  onError?: (error: ErrorResponse, variables: OrderExecutionCommand) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrderExecutionCommand) => executeOrder(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useCreateOrder = (props?: {
  onSuccess?: (data: Success, variables: NewOrderParams) => void
  onError?: (error: ErrorResponse, variables: NewOrderParams) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: NewOrderParams) => createOrder(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useUpdateOrder = (props?: {
  onSuccess?: (data: Success, variables: UpdateOrderParams) => void
  onError?: (error: ErrorResponse, variables: UpdateOrderParams) => void
}) => {
  const queryClient = useQueryClient()
  const setOpenOrders = useSetAtom(openOrdersRawAtom)
  return useMutation({
    mutationFn: (payload: UpdateOrderParams) => updateOrder(payload),
    onSuccess: (data, variables) => {
      setOpenOrders((prev) =>
        patchOpenOrder(
          prev,
          { accountId: variables.accountId, orderId: variables.orderId },
          { price: variables.price, quantity: variables.quantity }
        )
      )
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useCancelOrder = (props?: {
  onSuccess?: (data: Success, variables: CancelOrderParams) => void
  onError?: (error: ErrorResponse, variables: CancelOrderParams) => void
}) => {
  const queryClient = useQueryClient()
  const setOpenOrders = useSetAtom(openOrdersRawAtom)
  return useMutation({
    mutationFn: (payload: CancelOrderParams) => cancelOrder(payload),
    onSuccess: (data, variables) => {
      setOpenOrders((prev) =>
        removeOpenOrder(prev, { accountId: variables.accountId, orderId: variables.orderId })
      )
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      play('droplet')
      props?.onSuccess?.(data, variables)
    },
    onError: (error: ErrorResponse, variables) => {
      play('error')
      props?.onError?.(error, variables)
    },
  })
}

export const useExecuteOrderBatch = (props?: {
  onSuccess?: (data: Success, variables: OrderExecutionCommandList) => void
  onError?: (error: ErrorResponse, variables: OrderExecutionCommandList) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrderExecutionCommandList) => executeOrderBatch(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useCreateOrderBatch = (props?: {
  onSuccess?: (data: Success, variables: NewOrderParams[]) => void
  onError?: (error: ErrorResponse, variables: NewOrderParams[]) => void
  /** Which surface submitted the batch, for the `Submit Order` analytics event. */
  analyticsSource?: OrderSource
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (orders: NewOrderParams[]) => createOrderBatch(orders),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.all })
      play('success')
      // Read the accounts cache at success time instead of subscribing, so this
      // hook doesn't add a query observer to every order surface.
      const accounts = queryClient
        .getQueriesData<{ accounts: PublicAccount[] }>({
          queryKey: accountKeys.userAccounts(user?.userId),
        })
        .flatMap(([, cached]) => cached?.accounts ?? [])
      trackSubmittedOrders(variables, accounts, props?.analyticsSource ?? 'order-form')
      props?.onSuccess?.(data, variables)
    },
    onError: (error: ErrorResponse, variables) => {
      play('error')
      props?.onError?.(error, variables)
    },
  })
}
